// src/features/analytics/useAnalyticsTransfer.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type AnalyticsViewScope } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, TransferData } from './types';

export interface UseAnalyticsTransferResult {
  transfer: AnalyticsEnvelope<TransferData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAnalyticsTransfer(
  scope: AnalyticsViewScope | null,
  range: UtcRange,
  finalizedOnly: boolean
): UseAnalyticsTransferResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.transfer(
      scope?.kind ?? 'account',
      scope?.cid ?? 0,
      range.from,
      range.to,
      finalizedOnly
    ),
    queryFn: () => AnalyticsApi.getTransfer(scope!, range, finalizedOnly),
    enabled: scope !== null,
    staleTime: 60_000,
  });

  return {
    transfer: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
